import { contains, equals } from 'ramda';
import { useCallback, useEffect, useRef } from 'react';
import { filter } from 'rxjs/operators';
import { getId } from '../../../../utils/common';
import { isTargetInputField } from '../../../../utils/dom/domEvents';
import { useTrackActions } from '../../../../utils/hooks/bi/useTrackActions';
import { ActionClickOrigin } from '../../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useScrollStream } from '../../../../utils/hooks/useScrollStream';

const filterById = (item) => filter(equals(getId(item)));

export const useScrollHandler = (item, itemRef) => {
  const { scrollStream } = useScrollStream();
  useEffect(() => {
    if (!scrollStream) {
      return;
    }

    const subscriber = scrollStream
      .pipe(filterById(item))
      .subscribe(() => itemRef.current && itemRef.current.scrollIntoView());

    return () => subscriber.unsubscribe();
  }, []);
};

export const useMouseHandlers = (item, { onClick, onContextMenu, onDoubleClick }) => {
  const handleClick = useCallback(
    (event) => {
      if (!onClick) {
        return;
      }

      if (isTargetInputField(event)) {
        return;
      }

      event.stopPropagation();
      onClick(item, event);
    },
    [onClick, item],
  );

  const handleContextMenu = useCallback(
    (event) => {
      if (!onContextMenu) {
        return;
      }

      if (isTargetInputField(event)) {
        event.stopPropagation();
        return;
      }

      onContextMenu(item, event);
    },
    [onContextMenu, item],
  );

  const handleDoubleClick = useCallback(
    (event) => {
      if (!onDoubleClick) {
        return;
      }

      if (isTargetInputField(event)) {
        return;
      }

      event.stopPropagation();
      onDoubleClick(item, event);
    },
    [onDoubleClick, item],
  );

  return {
    handleClick,
    handleContextMenu,
    handleDoubleClick,
  };
};

export const useContextMenuHandlers = ({ item, selectedItems }) => {
  const contextTriggerRef = useRef<any>();
  const contextMenuOrigin = useRef<ActionClickOrigin>();

  const { trackActionsOpenClick } = useTrackActions();

  const getParamsForItemContextMenu = useCallback(() => {
    contextMenuOrigin.current = contextMenuOrigin.current || ActionClickOrigin.RightClick;

    trackActionsOpenClick(
      selectedItems && contains(item, selectedItems) ? selectedItems : [item],
      contextMenuOrigin.current,
    );

    const params = {
      contextMenuOrigin: contextMenuOrigin.current,
    };

    contextMenuOrigin.current = null;

    return params;
  }, [trackActionsOpenClick, item, selectedItems]);

  const openContextMenu = useCallback((event) => {
    contextMenuOrigin.current = ActionClickOrigin.ThreeDots;

    // because contextTrigger.handleContextClick does preventDefault and stopPropagation inside,
    // but we want event to bubble
    event.persist();
    setTimeout(() => {
      contextTriggerRef.current.handleContextClick(event);
    }, 0);
  }, []);

  return { contextTriggerRef, getParamsForItemContextMenu, openContextMenu };
};
